/**
 * documentStores - "Open in SharePoint / Google Drive" for a workspace file.
 *
 * The server places a copy in the organization's store and returns the link
 * (`apps/server/src/amoreg/documentStoreExportHttp.ts`). This module owns
 * the two halves the panel needs: which stores can take a copy for the open
 * thread (an atom per thread, so the buttons know whether to show, enable or
 * explain themselves), and the export itself.
 *
 * A deployment without document-store contracts answers 404 to the listing;
 * that is "no stores", not an error, so the panel simply shows no buttons.
 *
 * @module state/documentStores
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

export const DocumentStoreId = Schema.Literals(["sharepoint", "googledrive"]);
export type DocumentStoreId = typeof DocumentStoreId.Type;

export const DocumentStoreAvailability = Schema.Struct({
  id: DocumentStoreId,
  label: Schema.String,
  available: Schema.Boolean,
  reason: Schema.NullOr(Schema.String),
});
export type DocumentStoreAvailability = typeof DocumentStoreAvailability.Type;

const AvailabilityResponse = Schema.Struct({ stores: Schema.Array(DocumentStoreAvailability) });
const decodeAvailability = Schema.decodeUnknownEffect(AvailabilityResponse);

const NO_STORES: ReadonlyArray<DocumentStoreAvailability> = [];

const fetchAvailability = (threadId: string) =>
  Effect.gen(function* () {
    if (threadId.length === 0) return NO_STORES;
    const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/document-stores", { threadId });
    const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
    if (!response.ok) return NO_STORES;
    const json = yield* Effect.tryPromise(() => response.json());
    return (yield* decodeAvailability(json)).stores;
  }).pipe(Effect.orElseSucceed(() => NO_STORES));

const availabilityRuntime = Atom.runtime(Layer.empty);
const availabilityAtom = Atom.family((threadId: string) =>
  availabilityRuntime
    .atom(fetchAvailability(threadId))
    .pipe(Atom.withLabel(`web-amoreg:document-stores:${threadId}`)),
);

/** The stores for a thread; empty until known, and empty where there are none. */
export function useDocumentStores(threadId: string): ReadonlyArray<DocumentStoreAvailability> {
  const atom = useMemo(() => availabilityAtom(threadId), [threadId]);
  const result = useAtomValue(atom);
  return useMemo(() => Option.getOrElse(AsyncResult.value(result), () => NO_STORES), [result]);
}

const ExportResponse = Schema.Struct({ url: Schema.String });
const ExportFailure = Schema.Struct({ error: Schema.String });
const decodeExport = Schema.decodeUnknownOption(ExportResponse);
const decodeFailure = Schema.decodeUnknownOption(ExportFailure);

export class DocumentStoreExportError extends Error {
  override readonly name = "DocumentStoreExportError";
}

/**
 * Copy the file into the store and return where it opens. Errors carry the
 * server's sentence (already sanitized) so the toast can show why.
 */
export async function exportToDocumentStore(input: {
  readonly threadId: string;
  readonly path: string;
  readonly store: DocumentStoreId;
  readonly fetch?: typeof fetch;
}): Promise<string> {
  const doFetch = input.fetch ?? fetch;
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/document-stores/export");
  let response: Response;
  try {
    response = await doFetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ threadId: input.threadId, path: input.path, store: input.store }),
    });
  } catch {
    throw new DocumentStoreExportError("The server could not be reached.");
  }
  const json: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const failure = decodeFailure(json);
    throw new DocumentStoreExportError(
      Option.isSome(failure) ? failure.value.error : `The copy failed (HTTP ${response.status}).`,
    );
  }
  const result = decodeExport(json);
  if (Option.isNone(result)) {
    throw new DocumentStoreExportError("The store returned no link to open.");
  }
  return result.value.url;
}

/**
 * Open the copy in a new tab without tripping popup blockers: the tab is
 * opened synchronously in the click, then pointed at the link once the
 * export has finished. On failure the empty tab is closed again. The handle
 * is needed to navigate it, so `noopener` is applied by hand afterwards
 * rather than as a feature (which would return null).
 *
 * `beforeExport` runs after the tab exists and before the copy is made: the
 * spreadsheet editor uses it to write its pending edits first, so the copy
 * is what the person sees, not what was on disk 650 ms ago.
 */
export async function openInDocumentStore(
  input: Parameters<typeof exportToDocumentStore>[0] & {
    readonly beforeExport?: (() => Promise<void>) | undefined;
  },
  openWindow: (url: string) => Window | null = (url) => window.open(url, "_blank"),
): Promise<string> {
  const tab = openWindow("about:blank");
  try {
    await input.beforeExport?.();
    const url = await exportToDocumentStore(input);
    if (tab && !tab.closed) {
      tab.opener = null;
      tab.location.replace(url);
    } else {
      // Same shape as the early tab: blank first, opener severed, then the
      // link — so the store's page never holds a handle to this window.
      const fallback = openWindow("about:blank");
      if (fallback === null) {
        throw new DocumentStoreExportError(
          "The browser blocked the new tab. Allow pop-ups for this site and try again.",
        );
      }
      fallback.opener = null;
      fallback.location.replace(url);
    }
    return url;
  } catch (error) {
    if (tab && !tab.closed) tab.close();
    throw error;
  }
}
